/*
Copyright 2021-2024 New Vector Ltd.
SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
*/

import { logger } from "matrix-js-sdk/lib/logger";

import { useTranslation } from "react-i18next";
import { type FC, useState, useEffect } from "react";
import { isGuestOrigin } from "../auth/access";
import { getProductName } from "../branding";
import styles from "./HomePage.module.css";

import { useClientState } from "../ClientContext";
import { ErrorPage, LoadingPage } from "../FullScreenView";
import { UnauthenticatedView } from "./UnauthenticatedView";
import { RegisteredView } from "./RegisteredView";
import { usePageTitle } from "../usePageTitle";
import { widget } from "../widget";
import { CreateUserModal } from "../auth/admin/CreateUserModal";
import { backendFetch } from "../utils/backend";

export const HomePage: FC = () => {
  const { t } = useTranslation();
  usePageTitle(t("common.home"));

  const clientState = useClientState();
  const [isAdminModalOpen, setIsAdminModalOpen] = useState(false);
  const client =
    clientState?.state === "valid"
      ? clientState.authenticated?.client
      : undefined;
  const [ownerClient, setOwnerClient] = useState<typeof client>();
  useEffect(() => {
    let cancelled = false;
    setOwnerClient(undefined);
    if (client && !isGuestOrigin(window.location.origin)) {
      backendFetch(client, "/api/me")
        .then(async (res) => await res.json())
        .then((me) => {
          if (!cancelled && me.role === "owner") setOwnerClient(client);
        })
        .catch((error) =>
          logger.error("Could not load application role", error),
        );
    }
    return () => {
      cancelled = true;
    };
  }, [client]);

  if (isGuestOrigin(window.location.origin)) {
    return (
      <main className={styles.guestLanding}>
        <section className={styles.guestCard} aria-labelledby="guest-title">
          <h1 id="guest-title">{getProductName()}</h1>
          <p>
            {t(
              "instance.home_page.to_join_a_call_open_the_invitation_link_shared_by_a_participant",
            )}
          </p>
        </section>
      </main>
    );
  }

  if (!clientState) {
    return <LoadingPage />;
  } else if (clientState.state === "error") {
    return <ErrorPage widget={widget} error={clientState.error} />;
  } else {
    const isBoss = Boolean(client && ownerClient === client);

    return (
      <>
        {/* Модальное окно создания сотрудника.
            Сама кнопка открытия теперь перенесена в RegisteredView.tsx */}
        <CreateUserModal
          isOpen={isAdminModalOpen}
          onClose={() => setIsAdminModalOpen(false)}
        />

        {clientState.authenticated ? (
          <RegisteredView
            client={clientState.authenticated.client}
            isBoss={isBoss}
            onOpenAdminModal={() => setIsAdminModalOpen(true)}
          />
        ) : (
          <UnauthenticatedView />
        )}
      </>
    );
  }
};
